import { useEffect, useState } from "react";
import { AlertTriangle, Clock3, Link2, Receipt, RefreshCw } from "lucide-react";
import { Button } from "../ui/Button";
import { eur, fmtDate } from "../../lib/format";
import api from "../../lib/api";
import type { Anomaly, MissingReceipt, BankTransaction } from "../../types/bank";

const severityBadge: Record<string, string> = {
  HOOG: "bg-red-50 text-red-700 border-red-200",
  MIDDEL: "bg-amber-50 text-amber-700 border-amber-200",
  LAAG: "bg-slate-100 text-slate-600 border-slate-200",
};

const agingBadge: Record<string, string> = {
  Monitoren: "bg-slate-100 text-slate-600 border-slate-200",
  Onderzoeken: "bg-amber-50 text-amber-700 border-amber-200",
  Escaleren: "bg-orange-50 text-orange-700 border-orange-200",
  Stagnerend: "bg-red-50 text-red-700 border-red-200",
};

const kindLabels: Record<string, string> = {
  AMOUNT_DEVIATION: "Bedragafwijking",
  DUPLICATE_SUSPECT: "Mogelijke dubbele betaling",
  UNMATCHED_OLD: "Te lang niet gekoppeld",
  PAID_NOT_CLOSED: "Betaald maar niet gesloten",
};

/** Controle-paneel: afwijkingen (reconciliatie) + ontbrekende bonnetjes. */
export function ControlPanel({ onGoToTransactions }: { onGoToTransactions?: () => void }) {
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [receipts, setReceipts] = useState<MissingReceipt[]>([]);
  const [matched, setMatched] = useState<BankTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/api/bank-transactions/anomalies").then((r) => setAnomalies(r.data ?? [])),
      api.get("/api/bank-transactions/missing-receipts").then((r) => setReceipts(r.data ?? [])),
      api.get("/api/bank-transactions?matchStatus=MATCHED").then((r) => setMatched(r.data ?? [])),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <div className="space-y-6">
      <div className="surface-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <AlertTriangle size={22} className="text-amber-500" /> Afwijkingen ({anomalies.length})
          </h3>
          <Button variant="ghost" size="sm" onClick={load} loading={loading}>
            <RefreshCw size={16} /> Verversen
          </Button>
        </div>
        {anomalies.length === 0 && !loading ? (
          <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            Geen afwijkingen gevonden — alles is verwerkt.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {anomalies.map((a, i) => (
              <div key={i} className="py-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${severityBadge[a.severity]}`}>{a.severity}</span>
                    <span className="text-sm font-semibold text-slate-700">{kindLabels[a.kind] ?? a.kind}</span>
                    {a.aging && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${agingBadge[a.aging]}`}>
                        <Clock3 size={12} className="inline -mt-0.5 mr-1" />
                        {a.aging} ({a.ageDays}d)
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{a.message}</p>
                </div>
                {a.bankTransactionId && (
                  <Button variant="ghost" size="sm" onClick={onGoToTransactions}>
                    Naar transactie
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="surface-card p-6">
        <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-4">
          <Receipt size={22} className="text-brand-500" /> Ontbrekende bonnetjes ({receipts.length})
        </h3>
        {receipts.length === 0 && !loading ? (
          <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            Alle uitgaven hebben een bon of bewijsstuk.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {receipts.map((r) => (
              <div key={r.expenseId} className="py-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-700 truncate">
                    {r.description}{r.merchant ? ` — ${r.merchant}` : ""} · <span className={r.amountEur >= 0 ? "text-rose-600" : "text-emerald-600"}>{eur(r.amountEur)}</span>
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {fmtDate(r.date)}
                    {r.viaBank && " · via bank geboekt (geen bronbon)"}
                    {r.hint && ` · ${r.hint}`}
                  </p>
                </div>
                {onGoToTransactions && r.viaBank && (
                  <Button variant="ghost" size="sm" onClick={onGoToTransactions}>
                    Matchen
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="surface-card p-6">
        <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-4">
          <Link2 size={22} className="text-emerald-600" /> Gekoppelde transacties ({matched.length})
        </h3>
        {matched.length === 0 && !loading ? (
          <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
            Nog geen transacties gekoppeld. Gebruik Auto-match op het tabblad "Transacties".
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {matched.map((t) => (
              <div key={t.id} className="py-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-700 truncate">
                    {t.counterparty || t.description} ·{" "}
                    <span className={Number(t.amountEur) >= 0 ? "text-emerald-600" : "text-rose-600"}>{eur(Number(t.amountEur))}</span>
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {fmtDate(t.date)}
                    {t.invoice ? ` · Factuur ${t.invoice.number}${t.invoice.contact?.companyName ? ` — ${t.invoice.contact.companyName}` : ""}` : ""}
                    {t.expense ? ` · Uitgave: ${t.expense.description}` : ""}
                    {!t.invoice && !t.expense && t.payment ? " · Betaling geboekt" : ""}
                  </p>
                </div>
                {onGoToTransactions && (
                  <Button variant="ghost" size="sm" onClick={onGoToTransactions}>
                    Naar transactie
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}